import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { clearCurrentUser, getCurrentUser, setCurrentUser } from '../utils/userSessionApi';

const SessionContext = createContext(null);

export const SessionProvider = ({ children }) => {
  const [cookies, , removeCookie] = useCookies(['invoice_csrf']);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(cookies.invoice_csrf));

  useEffect(() => {
    let active = true;
    if (!cookies.invoice_csrf) {
      clearCurrentUser();
      setUser(null);
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    getCurrentUser()
      .then((currentUser) => {
        if (active) setUser(currentUser);
      })
      .catch(() => {
        if (active) {
          clearCurrentUser();
          setUser(null);
          removeCookie('invoice_csrf', { path: '/' });
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [cookies.invoice_csrf, removeCookie]);

  const value = useMemo(() => ({
    user,
    role: user?.role || null,
    loading,
    clearSession: () => {
      clearCurrentUser();
      setUser(null);
    },
    updateSessionUser: (nextUser) => {
      setCurrentUser(nextUser);
      setUser(nextUser);
      setLoading(false);
    },
  }), [loading, user]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
};
